import { LogoutOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Button, Col, Form, message, Radio, Row, Typography } from "antd";
import * as moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { AreaManagerCard } from "../../../../Components/Cards/Onboarding/AreaManager";
import { Loader } from "../../../../Components/Loader";
import { CustIcon } from "../../../../Components/Svgs";
import {
  DELETE_USER,
  DELETE_USER_LOCATION_MAP,
  INSERT_ROLE_LOCATION_MAP,
  INSERT_USER,
  UPDATE_USER_DETAILS,
  UPDATE_USER_LAST_SCREEN,
} from "../../../../Graphql/mutations";
import {
  GET_LAST_SCREEN,
  GET_LOCATIONS_AND_AREA_MANAGER_ID,
} from "../../../../Graphql/queries";
import {
  generateRandomPassword,
  logout,
} from "../../../../Utils/functions/common";
import "./AreaManager.less";

const { Title } = Typography;

export const AreaManagerOnboarding = () => {
  const history = useHistory();
  const [form] = Form.useForm();

  const [hasAreaManagers, setHasAreaManagers] = useState(true);
  const [areaManagerCount, setAreaManagerCount] = useState(1);
  const [workingHours, setWorkingHours] = useState([]);

  const layout = {
    labelCol: {
      span: 24,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const {
    data,
    loading: fetchingData,
    previousData,
  } = useQuery(GET_LOCATIONS_AND_AREA_MANAGER_ID, {
    variables: {
      owner_id: localStorage.getItem("user_id"),
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: lastScreenData } = useQuery(GET_LAST_SCREEN, {
    variables: {
      user_id: localStorage.getItem("user_id"),
    },
    fetchPolicy: "cache-and-network",
  });

  const [UPDATE_USER_LAST_SCREEN_MUTATION] = useMutation(
    UPDATE_USER_LAST_SCREEN
  );

  const [INSERT_USER_MUTATION, { loading: isInsertingUser }] =
    useMutation(INSERT_USER);

  const [UPDATE_USER_DETAILS_MUTATION, { loading: isUpdatingUser }] =
    useMutation(UPDATE_USER_DETAILS);

  const [DELETE_USER_MUTATION, { loading: isDeletingUser }] =
    useMutation(DELETE_USER);

  const [
    DELETE_USER_LOCATION_MAP_MUTATION,
    { loading: isDeletingLocationMap },
  ] = useMutation(DELETE_USER_LOCATION_MAP);

  const [
    INSERT_ROLE_LOCATION_MAP_MUTATION,
    { loading: isInsertingUserRoleLocationMap },
  ] = useMutation(INSERT_ROLE_LOCATION_MAP);

  useEffect(() => {
    if (
      lastScreenData &&
      lastScreenData?.getUsersById?.last_screen !== history.location.pathname
    ) {
      UPDATE_USER_LAST_SCREEN_MUTATION({
        variables: {
          user_id: localStorage.getItem("user_id"),
          last_screen: history.location.pathname,
        },
      });
    }
  }, [lastScreenData]);

  useEffect(() => {
    if (data?.areaManagers?.length > 0) {
      setAreaManagerCount(data.areaManagers.length);
      let values = {};
      data?.areaManagers.forEach((singleManager, i) => {
        let formValues = {};
        formValues[`firstName_${i}`] = singleManager.first_name;
        formValues[`lastName_${i}`] = singleManager.last_name;
        formValues[`email_${i}`] = singleManager.email;
        formValues[`phone_${i}`] = singleManager.phone;
        formValues[`employee_code_${i}`] = singleManager.employee_code;
        formValues[`user_id_${i}`] = singleManager.user_id;
        let locationIdList = [];
        singleManager.locations.forEach(({ locationDetails }) => {
          locationIdList.push(locationDetails.location_id);
        });
        formValues[`locations_${i}`] = locationIdList;
        values = { ...values, ...formValues };
      });
      form.setFieldsValue(values);
    }
    if (data?.locations?.length > 0) {
      calculateAreaManagerWorkingHours();
    }
  }, [data]);

  const calculateAreaManagerWorkingHours = () => {
    let startTime = "";
    let endTime = "";
    data.locations.forEach(({ location_timings }) => {
      location_timings.forEach(({ opening_time, closing_time }) => {
        if (!startTime) {
          startTime = opening_time;
        }
        if (!endTime) {
          endTime = closing_time;
        }
        if (opening_time < startTime) {
          startTime = opening_time;
        }
        if (closing_time > endTime) {
          endTime = closing_time;
        }
      });
    });
    setWorkingHours(
      [...Array.from(Array(7).keys())].flatMap((i) => [
        {
          working_day: moment().set("day", i).format("dddd"),
          start_time: startTime,
          end_time: endTime,
        },
      ])
    );
  };

  const onRemoveManager = (i) => {
    setAreaManagerCount((currentCount) => currentCount - 1);
  };

  const onFinish = async (values) => {
    let insertManagersArray = [];
    let updateManagersArray = [];
    for (let i = 0; i < areaManagerCount; i++) {
      if (!values[`user_id_${i}`]) {
        insertManagersArray.push({
          first_name: values[`firstName_${i}`],
          last_name: values[`lastName_${i}`],
          email: values[`email_${i}`],
          phone: values[`phone_${i}`],
          locations: values[`locations_${i}`],
          employee_code: values[`employee_code_${i}`],
        });
      } else {
        updateManagersArray.push({
          user_id: values[`user_id_${i}`],
          first_name: values[`firstName_${i}`],
          last_name: values[`lastName_${i}`],
          email: values[`email_${i}`],
          phone: values[`phone_${i}`],
          locations: values[`locations_${i}`],
          employee_code: values[`employee_code_${i}`],
          old_locations: data?.areaManagers[i].locations.flatMap(
            (singleLocation) => [singleLocation.locationDetails.location_id]
          ),
        });
      }
    }
    try {
      const insertSuccess = await insertNewAreaManagers(insertManagersArray);
      const updateSuccess = await updateManagerDetails(updateManagersArray);
      if (insertSuccess && updateSuccess) {
        message.success("Area Manager Data Saved");
        history.push("/on-boarding/employee-roles");
      }
    } catch (error) {}
  };

  const insertNewAreaManagers = async (insertManagersArray) => {
    if (insertManagersArray.length === 0) {
      return true;
    }
    let insertParams = insertManagersArray.flatMap(
      ({ first_name, last_name, email, phone, locations, employee_code }) => [
        {
          first_name,
          last_name,
          email,
          phone,
          employee_code,
          password: generateRandomPassword(),
          user_location_maps: locations.flatMap((location_id) => [
            {
              business_id: data.businesses[0].business_id,
              job_role_id: data.roles[0].role_id,
              location_id,
            },
          ]),
          user_shifts: workingHours,
        },
      ]
    );
    let insertSuccess = await INSERT_USER_MUTATION({
      variables: {
        objects: insertParams,
      },
    });
    if (insertSuccess.data.insert_employees.status === "OK") {
      return true;
    } else {
      return false;
    }
  };

  const updateManagerDetails = async (updateManagersArray) => {
    if (updateManagersArray.length === 0) {
      return true;
    } else {
      let updateSuccessFlag = await Promise.all(
        updateManagersArray.map(
          async ({
            first_name,
            last_name,
            email,
            phone,
            employee_code,
            user_id,
            old_locations,
            locations,
          }) => {
            const userUpdateResponse = await UPDATE_USER_DETAILS_MUTATION({
              variables: {
                user_id,
                user_details: {
                  first_name,
                  last_name,
                  email,
                  phone,
                  employee_code,
                },
              },
            });
            if (!userUpdateResponse.errors) {
              let removedLocations = old_locations.filter(
                (singleLocationId) => {
                  return locations.indexOf(singleLocationId) === -1;
                }
              );
              let addedLocations = locations.filter((singleLocationId) => {
                return old_locations.indexOf(singleLocationId) === -1;
              });
              let areLocationsAdded = await addLocationsToManager({
                addedLocations,
                user_id,
              });
              let areLocationsRemoved = await removeLocationsFromManager({
                removedLocations,
                user_id,
              });
              if (areLocationsAdded && areLocationsRemoved) {
                return true;
              } else {
                return false;
              }
            } else {
              return false;
            }
          }
        )
      );
      if (updateSuccessFlag.includes(false)) {
        return false;
      } else {
        return true;
      }
    }
  };

  const addLocationsToManager = async ({ addedLocations, user_id }) => {
    if (addedLocations.length === 0) {
      return true;
    }
    let locationAddedSuccess = await Promise.all(
      addedLocations.map(async (locationId) => {
        try {
          const addedResponse = await INSERT_ROLE_LOCATION_MAP_MUTATION({
            variables: {
              user_id,
              location_id: locationId,
              business_id: data.businesses[0].business_id,
              job_role_id: data.roles[0].role_id,
            },
          });
          if (addedResponse.errors) {
            return false;
          } else {
            return true;
          }
        } catch (error) {}
      })
    );
    if (!locationAddedSuccess.includes(false)) {
      return true;
    } else {
      return false;
    }
  };

  const removeLocationsFromManager = async ({ removedLocations, user_id }) => {
    if (removedLocations.length === 0) {
      return true;
    }
    let locationRemovedSuccess = await Promise.all(
      removedLocations.map(async (locationId) => {
        try {
          const removedResponse = await DELETE_USER_LOCATION_MAP_MUTATION({
            variables: {
              user_id,
              location_id: locationId,
              business_id: data.businesses[0].business_id,
              job_role_id: data.roles[0].role_id,
            },
          });
          if (removedResponse.errors) {
            return false;
          } else {
            return true;
          }
        } catch (error) {}
      })
    );
    if (!locationRemovedSuccess.includes(false)) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <div className="onBoardingRight">
      {fetchingData && !previousData ? (
        <Loader />
      ) : (
        <div className="areaManager pt-lg-0">
          <div className="pl-lg-5">
            <div className="d-flex justify-content-between align-items-start">
              <Title level={2}>
                <CustIcon type="left-arrow" onClick={() => history.goBack()} />
                Setup your Area Manager’s Access
              </Title>
              <div
                className="d-flex align-items-center cursorPointer txtdark"
                onClick={() => logout()}
              >
                <CustIcon type="logout-alt" className="fz-18" />
                <span className="pl-2">Logout</span>
              </div>
            </div>
            <p className="subTitle">
              Enter the details for your Area manager(s) for your business.
            </p>
          </div>
          <div className="mt-4">
            <p className="question fontmed">
              Do you have Area managers who are handling multiple locations?
            </p>
            <Radio.Group
              value={hasAreaManagers}
              onChange={({ target: { value } }) => {
                form.resetFields();
                setHasAreaManagers(value);
              }}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </div>
          {hasAreaManagers && (
            <div className="mt-3 areaManagerBlock">
              <Row className="justify-content-between align-items-center mb-3">
                <Col>
                  <p className="subTitle fontmed mb-0">Area Managers</p>
                </Col>
                <Col>
                  <Button
                    onClick={() =>
                      setAreaManagerCount((currentCount) => currentCount + 1)
                    }
                    shape="round"
                    size="large"
                    className="addmanagerBtn"
                  >
                    <CustIcon type="userplus" className="mr-1" />
                    Add Manager
                  </Button>
                </Col>
              </Row>
              <Form form={form} onFinish={onFinish} {...layout}>
                {[...Array(areaManagerCount)].map((_singleManager, index) => {
                  return (
                    <Fragment key={index}>
                      <AreaManagerCard
                        id={index}
                        deleteManager={onRemoveManager}
                        form={form}
                        count={areaManagerCount}
                        locationList={data.locations}
                      />
                    </Fragment>
                  );
                })}
              </Form>
            </div>
          )}
          <div className="buttonSec mt-5 d-flex justify-content-center">
            <Button
              loading={isInsertingUserRoleLocationMap || isInsertingUser}
              type="primary"
              shape="round"
              size="large"
              onClick={() => {
                !hasAreaManagers
                  ? history.push("/on-boarding/employee-roles")
                  : form.submit();
              }}
            >
              {isInsertingUserRoleLocationMap || isInsertingUser
                ? "Submitting"
                : "Continue"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
